'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import Badge from '@/components/atoms/badge';
import { classnames } from '@/utils/classnames/classnames';
import { GalleryProps } from './types';

// EWM-193 — Dan's gallery layout: large primary image + horizontal thumbnail
// strip (max 5 on desktop) + lightbox. Replaces the legacy react-slick
// vertical/horizontal slider composition.
//
// No-image / single-image / multi-image branches handled inline. Lightbox
// modal is a child node inside this component (no other consumers planned).
const DESKTOP_THUMBNAIL_CAP = 5;

const Gallery = ({ className, images, productName, badge }: GalleryProps) => {
  const translate = useTranslations();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const zoomTriggerRef = useRef<HTMLButtonElement>(null);

  const hasImages = images.length > 0;
  const isMulti = images.length > 1;

  // EWM-760 (was a setState-in-effect on `[images]`) — adjust-state-during-render:
  // reset the index when the `images` reference changes (e.g. variant switch),
  // synchronously in the same render instead of a follow-up effect + cascading
  // render. `prevImages` is compared by reference exactly like the old effect's
  // dependency array, so it fires on the identical trigger.
  const [prevImages, setPrevImages] = useState(images);
  if (prevImages !== images) {
    setPrevImages(images);
    setSelectedIndex(0);
  }

  // Cyclic navigation, shared by the arrow keys + the lightbox prev/next
  // buttons. No-op when there's a single image.
  const goPrev = useCallback(() => {
    setSelectedIndex((idx) => (idx - 1 + images.length) % images.length);
  }, [images.length]);
  const goNext = useCallback(() => {
    setSelectedIndex((idx) => (idx + 1) % images.length);
  }, [images.length]);

  // EWM-591 — open the lightbox at a specific image (used by the main image
  // and every page thumbnail; the AC wants a thumbnail click to open the
  // lightbox, not just swap the primary image).
  const openZoomAt = useCallback((index: number) => {
    setSelectedIndex(index);
    setZoomOpen(true);
  }, []);

  // Lightbox keyboard handlers: Escape closes, arrow keys navigate.
  useEffect(() => {
    if (!zoomOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setZoomOpen(false);
      } else if (e.key === 'ArrowLeft' && isMulti) {
        goPrev();
      } else if (e.key === 'ArrowRight' && isMulti) {
        goNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomOpen, isMulti, goPrev, goNext]);

  // Return focus to the zoom trigger when the lightbox closes.
  //
  // EWM-847 — guard on a real open -> close transition. Unguarded this also ran
  // on MOUNT (`zoomOpen` starts false, nothing has closed) and grabbed focus on
  // every PDP load. Same one-line defect as the purchase-list CTA; here it
  // didn't visibly scroll the page only because the main image sits near the top
  // and is already in view, but a keyboard user's focus still landed on the zoom
  // button before they touched anything.
  const wasZoomOpenRef = useRef(false);
  useEffect(() => {
    if (!zoomOpen && wasZoomOpenRef.current) {
      zoomTriggerRef.current?.focus();
    }
    wasZoomOpenRef.current = zoomOpen;
  }, [zoomOpen]);

  if (!hasImages) {
    return (
      <div
        className={classnames(
          'flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-400',
          className,
        )}
        data-testid="gallery-empty"
      >
        <PhotoIcon className="size-16" aria-hidden />
        <p className="text-14">{translate('pdp.no-image')}</p>
      </div>
    );
  }

  const desktopThumbnails = images.slice(0, DESKTOP_THUMBNAIL_CAP);

  return (
    <div className={classnames('flex flex-col gap-4', className)}>
      <button
        ref={zoomTriggerRef}
        type="button"
        onClick={() => setZoomOpen(true)}
        aria-label={translate('pdp.zoom-image')}
        className="group relative aspect-square w-full cursor-zoom-in overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        {/* EWM-657 — marketing badge, upper-left of the primary image, matching
            the PLP tile. Inset (not flush) so it sits cleanly inside the rounded,
            overflow-hidden image frame. */}
        {badge && (
          <Badge variant={badge} className="absolute top-3 left-3 z-10">
            {badge.replace(/-/g, ' ')}
          </Badge>
        )}
        <Image
          fill
          sizes="(min-width: 1024px) 540px, 100vw"
          className="object-contain p-8 transition-transform duration-300 group-hover:scale-105"
          src={images[selectedIndex].url}
          alt={images[selectedIndex].altText ?? productName}
          loading="eager"
        />
        <span className="absolute top-3 right-3 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white/90 p-2 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
          <MagnifyingGlassPlusIcon className="text-primary size-5" aria-hidden />
        </span>
      </button>

      {isMulti && (
        <div className="flex gap-3 overflow-x-auto md:flex-wrap" role="list">
          {desktopThumbnails.map((image, index) => {
            const isActive = index === selectedIndex;
            return (
              <button
                key={`${image.url || 'thumb'}-${index}`}
                type="button"
                onClick={() => openZoomAt(index)}
                aria-label={translate('pdp.thumbnail-n', { n: index + 1 })}
                aria-current={isActive ? 'true' : undefined}
                className={classnames(
                  'relative size-20 shrink-0 overflow-hidden rounded-lg border-2 bg-white transition-colors',
                  isActive ? 'border-primary' : 'border-gray-200 hover:border-gray-400',
                )}
              >
                <Image
                  fill
                  sizes="80px"
                  className="object-contain p-1"
                  src={image.url}
                  alt={image.altText ?? ''}
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      )}

      {zoomOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={translate('pdp.zoom-image')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setZoomOpen(false)}
        >
          {/* EWM-591 — ONE white lightbox panel that contains EVERYTHING: the
              counter, the image (with its overlaid prev/next arrows + close),
              and the thumbnail strip. QA (EWM-591 re-open) flagged the previous
              layout as "spread out" because the counter/arrows/close/strip
              floated on the backdrop, detached from the image; pulling them all
              inside a single bounded surface fixes that. Clicking the panel is a
              no-op; clicking the backdrop closes. */}
          <div
            className="relative flex w-[90vw] max-w-[70vh] flex-col gap-3 rounded-lg bg-white p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setZoomOpen(false)}
              aria-label={translate('pdp.close-zoom')}
              className="bg-primary hover:bg-primary/90 absolute top-3 right-3 z-10 inline-flex size-10 items-center justify-center rounded-full text-white shadow-lg transition-colors"
            >
              <XMarkIcon className="size-6" aria-hidden />
            </button>

            {/* EWM-591 — image counter ("2 of 5"), inside the panel. */}
            {isMulti && (
              <p className="text-14 shrink-0 text-center font-medium text-gray-700">
                {translate('pdp.image-counter', { current: selectedIndex + 1, total: images.length })}
              </p>
            )}

            {/* Square image area, capped at 60vh so the panel (counter + image
                + strip + padding) always fits the viewport height. */}
            <div className="relative aspect-square max-h-[60vh] w-full overflow-hidden rounded-md">
              {/* EWM-591 — visible prev/next arrows for mouse users, overlaid on
                  the image edges (keyboard arrows still work via the keydown
                  handler). ring + shadow for contrast against the white image. */}
              {isMulti && (
                <>
                  <button
                    type="button"
                    onClick={goPrev}
                    aria-label={translate('pdp.previous-image')}
                    className="absolute top-1/2 left-2 z-10 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-800 shadow-md ring-1 ring-black/10 transition-colors hover:bg-gray-100"
                  >
                    <ChevronLeftIcon className="size-6" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    aria-label={translate('pdp.next-image')}
                    className="absolute top-1/2 right-2 z-10 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-800 shadow-md ring-1 ring-black/10 transition-colors hover:bg-gray-100"
                  >
                    <ChevronRightIcon className="size-6" aria-hidden />
                  </button>
                </>
              )}

              <Image
                fill
                sizes="(min-width: 768px) 70vh, 90vw"
                className="object-contain p-4 md:p-6"
                src={images[selectedIndex].url}
                alt={images[selectedIndex].altText ?? productName}
                loading="eager"
              />
            </div>

            {/* EWM-591 — bottom thumbnail strip, inside the panel. Unlike the
                page strip (capped at 5), this shows EVERY image so the lightbox
                is the full browse surface. Scrolls horizontally when it
                overflows the panel width. */}
            {isMulti && (
              <div className="flex w-full shrink-0 justify-center gap-2 overflow-x-auto py-1" role="list">
                {images.map((image, index) => {
                  const isActive = index === selectedIndex;
                  return (
                    <button
                      key={`lightbox-${image.url || 'thumb'}-${index}`}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      aria-label={translate('pdp.thumbnail-n', { n: index + 1 })}
                      aria-current={isActive ? 'true' : undefined}
                      className={classnames(
                        'relative size-16 shrink-0 overflow-hidden rounded-md border-2 bg-white transition-colors',
                        isActive ? 'border-primary' : 'border-gray-200 hover:border-gray-400',
                      )}
                    >
                      <Image
                        fill
                        sizes="64px"
                        className="object-contain p-1"
                        src={image.url}
                        alt={image.altText ?? ''}
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
